import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Chip, IconButton, ListItemIcon, Menu, MenuItem, Paper, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from 'react';
import { useSnackbar } from '../../../core/contexts/SnackbarContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CardIndustrializadoCardapio } from '../../components/CardIndustrializadoCardapio';
import { CardPesoCardapio } from '../../components/CardPesoCardapio';
import { CardPizzaCardapio } from '../../components/CardPizzaCardapio';
import { CardPreparadoCardapio } from '../../components/CardPreparadoCardapio';
import { ConfirmarAcaoTextoModal } from '../../../core/components/ConfirmarAcaoTextoModal';
import { ExcluirProdutoModal } from '../../components/ExcluirProdutoModal';
import { TipoProduto } from '../../enum/TipoProduto';
import { useProdutos } from '../../hooks/useProdutos';
import { ICategoria } from "../../interfaces/ICategoria";
import { IProduto } from '../../interfaces/IProduto';
import { BodyCardContainer, BodySemProdutosCardContainer, ButtonsHeaderBox, CardContainer, ContextosBox, DetalhesHeaderBox, HeaderCardContainer, PeriodosExibicaoCardContainer } from "./styles";
import { useTamanhoTela } from '../../../core/hooks/useTamanhoTela';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { IMarketplace } from '../../../marketplace/interfaces/IMarketplace';
import { TipoMarketplaceCor, TipoMarketplaceTitulo } from '../../../marketplace/enum/TipoMarketplace';
import { ArrowDropDown, CopyAll, DragIndicator } from '@mui/icons-material';
import { v4 } from 'uuid';
import { useLetras } from '../../../core/hooks/useLetras';
import { useCategorias } from '../../hooks/useCategorias';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ICategoriaCard {
    categoria: ICategoria;
    marketplaces?: IMarketplace[];
    categorias: ICategoria[];
    excluirCategoria: (categoria: ICategoria) => Promise<boolean>
    handleRefresh: () => void;
}

export const CategoriaCard = ({ categoria, excluirCategoria, handleRefresh, categorias, marketplaces }: ICategoriaCard) => {
    const { excluir } = useProdutos();
    const { duplicar } = useCategorias();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const [dadosCategoria, setDadosCategoria] = useState<ICategoria | undefined>(undefined);
    const [produtoExclusao, setProdutoExclusao] = useState<IProduto | undefined>(undefined);
    const [excluirOpen, setExcluirOpen] = useState<boolean>(false);
    const [loadingExcluir, setLoadingExcluir] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const categoriaUuid = searchParams.get('categoriaUuid');

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: categoria.uuid as string });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const { modeloTela } = useTamanhoTela();

    const { titulo, maiusculas } = useLetras();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOpenExcluir = () => setExcluirOpen(true);
    const handleCloseExcluir = () => {
        setExcluirOpen(false);
        setTimeout(() => handleRefresh(), 300);
    }

    const handleCloseExcluirProduto = () => {
        setProdutoExclusao(undefined);
        setTimeout(() => handleRefresh(), 300);
    }

    const handleOpenExcluirProduto = (produto: IProduto) => {
        setProdutoExclusao(produto);
    }

    const handleExcluirProduto = async (uuid: string) => {
        if (!dadosCategoria || !dadosCategoria.uuid) {
            return false;
        }

        return await excluir(dadosCategoria.uuid, uuid);
    }

    const handleAccordionChange = (isExpanded: boolean) => {
        if (isExpanded) {
            searchParams.set('categoriaUuid', categoria.uuid as string);
            setSearchParams(searchParams);
        } else {
            searchParams.delete('categoriaUuid');
            setSearchParams(searchParams);
        }
    };

    useEffect(() => {
        if (categoria) {
            setDadosCategoria(categoria);
        }
    }, [categoria])

    const handleConfirmarExclusao = async () => {
        setLoadingExcluir(true);
        const resultado = await excluirCategoria(dadosCategoria!);
        setLoadingExcluir(false);

        if (!resultado) {
            return snackbar("Ocorreu um erro ao excluir a categoria", { severity: "error" });
        }

        handleCloseExcluir();
        snackbar("Categoria excluída com sucesso", { severity: "success" });
    };

    const handleDuplicarCategoria = async () => {
        if(!categoria?.uuid) {
            return;
        }

        await duplicar(categoria.uuid);

        handleRefresh();
    }

    return (
        <Accordion
            ref={setNodeRef}
            style={style}
            sx={{ width: "100%" }}
            key={categoria.uuid}
            expanded={categoriaUuid === categoria.uuid}
            onChange={(_, isExpanded) => handleAccordionChange(isExpanded)}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 2, width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box
                        {...attributes}
                        {...listeners}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        <DragIndicator 
                            sx={{ 
                                cursor: "grab", 
                                mr: 2,
                                color: "action.active",
                                "&:active": {
                                    cursor: "grabbing"
                                }
                            }} 
                        />
                    </Box>
                    <Typography variant="h6" fontWeight={"600"}>
                        {dadosCategoria?.descricao?.toUpperCase()}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ width: "100%" }}>
                <HeaderCardContainer variant='outlined'>
                    <DetalhesHeaderBox>
                        <Typography fontWeight={"bold"}>
                            Posição:
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {categoria?.posicao}
                        </Box>
                    </DetalhesHeaderBox>
                    <DetalhesHeaderBox>
                        <Typography fontWeight={"bold"}>
                            Contextos:
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {dadosCategoria?.contextos?.length ? (
                                <Typography sx={{ overflowX: "auto", fontSize: 12 }}>
                                    {maiusculas(dadosCategoria?.contextos?.join(`, `))}
                                </Typography>
                            ) : "-"}
                        </Box>
                    </DetalhesHeaderBox>
                    <DetalhesHeaderBox>
                        <Typography fontWeight={"bold"}>
                            Marketplaces:
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {marketplaces?.length ? marketplaces?.map(marketplace => (
                                <Tooltip title={marketplace.nome} sx={{ mr: 0.3 }}>
                                    <Chip
                                        size='small'
                                        label={TipoMarketplaceTitulo[marketplace.tipo]}
                                        style={{ background: TipoMarketplaceCor[marketplace.tipo], color: "white" }}
                                    />
                                </Tooltip>
                            )) : "-"}
                        </Box>
                    </DetalhesHeaderBox>
                    <DetalhesHeaderBox>
                        <Typography fontWeight={"bold"}>
                            Nº de produtos:
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography>{dadosCategoria?.produtos?.filter(p => !p.apenasCombo).length}</Typography>
                        </Box>
                    </DetalhesHeaderBox>
                    <ButtonsHeaderBox>
                        {dadosCategoria && (
                            <>
                                <Button
                                    onClick={handleClick}
                                    variant="contained"
                                    aria-label="more"
                                    id={`categoria-menu-button-${dadosCategoria.uuid}`}
                                    aria-controls={open ? `categoria-menu-${dadosCategoria.uuid}` : undefined}
                                    aria-expanded={open ? 'true' : undefined}
                                    aria-haspopup="true"
                                    endIcon={
                                        <ArrowDropDown
                                            sx={{ transform: open ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                                        />
                                    }
                                >
                                    AÇÕES
                                </Button>
                                <Menu
                                    id={`categoria-menu-${dadosCategoria.uuid}`}
                                    MenuListProps={{
                                        'aria-labelledby': `categoria-menu-${dadosCategoria.uuid}`,
                                    }}
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                >
                                    <Tooltip title='Cadastre um novo produto na categoria'>
                                        <MenuItem onClick={() => navigate(`categorias/${dadosCategoria.uuid}/produtos`)}>
                                            <ListItemIcon>
                                                <AddIcon fontSize="small" />
                                            </ListItemIcon>
                                            <Typography variant="inherit" noWrap>
                                                Produto
                                            </Typography>
                                        </MenuItem>
                                    </Tooltip>
                                    <Tooltip title='Edite a categoria'>
                                        <MenuItem onClick={() => navigate(`categorias/${dadosCategoria.uuid}`)}>
                                            <ListItemIcon>
                                                <EditIcon fontSize="small" />
                                            </ListItemIcon>
                                            <Typography variant="inherit" noWrap>
                                                Editar
                                            </Typography>
                                        </MenuItem>
                                    </Tooltip>
                                    <Tooltip title='Exclua a categoria'>
                                        <MenuItem onClick={() => handleOpenExcluir()}>
                                            <ListItemIcon>
                                                <DeleteIcon fontSize="small" />
                                            </ListItemIcon>
                                            <Typography variant="inherit" noWrap>
                                                Excluir
                                            </Typography>
                                        </MenuItem>
                                    </Tooltip>
                                    <Tooltip title='Duplicar categoria'>
                                        <MenuItem onClick={handleDuplicarCategoria}>
                                            <ListItemIcon>
                                                <CopyAll fontSize="small" />
                                            </ListItemIcon>
                                            <Typography variant="inherit" noWrap>
                                                Duplicar
                                            </Typography>
                                        </MenuItem>
                                    </Tooltip>
                                </Menu>
                                <ConfirmarAcaoTextoModal
                                    open={excluirOpen}
                                    titulo={`Excluir categoria: ${dadosCategoria.descricao}`}
                                    mensagem={<>Para confirmar a exclusão, digite <b>{dadosCategoria.descricao}</b> no campo.</>}
                                    parametro={dadosCategoria.descricao}
                                    cancelar={handleCloseExcluir}
                                    confirmar={handleConfirmarExclusao}
                                    loading={loadingExcluir}
                                />
                                <ExcluirProdutoModal
                                    handleRefresh={handleRefresh}
                                    produto={produtoExclusao}
                                    open={produtoExclusao ? true : false}
                                    excluirProduto={handleExcluirProduto}
                                    handleClose={handleCloseExcluirProduto}
                                />
                            </>
                        )}
                    </ButtonsHeaderBox>
                </HeaderCardContainer>
                {categoria?.periodosExibicao?.length ? (
                    <PeriodosExibicaoCardContainer variant='outlined'>
                        {categoria.periodosExibicao?.map(periodo => {
                            return (
                                <DetalhesHeaderBox>
                                    <Typography sx={{ fontSize: 14 }}>
                                        {titulo(periodo.diaSemana)}
                                    </Typography>
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "column"
                                    }}>
                                        {periodo?.horarios?.map(horario => {
                                            return (
                                                <Typography color={"GrayText"} sx={{ fontSize: 12 }}>
                                                    {horario.inicio} - {horario.fim}
                                                </Typography>
                                            );
                                        })}
                                    </Box>
                                </DetalhesHeaderBox>
                            )
                        })}
                    </PeriodosExibicaoCardContainer>
                ) : ''}
                {modeloTela === "mobile" && (
                    <Tooltip title='Contextos onde a categoria será apresentada'>
                        <ContextosBox>
                            {dadosCategoria?.contextos?.map(contexto => (
                                <Chip key={contexto} label={contexto.toUpperCase()} />
                            ))}
                        </ContextosBox>
                    </Tooltip>
                )}
                {dadosCategoria?.produtos?.filter(p => !p.apenasCombo).length ? (
                    <BodyCardContainer container spacing={1}>
                        {dadosCategoria?.produtos?.filter(p => !p.apenasCombo).map(produto => {
                            if (produto.tipo === TipoProduto.pizza && dadosCategoria.uuid) {
                                return <CardPizzaCardapio
                                    key={produto.uuid}
                                    handleRefresh={handleRefresh}
                                    categorias={categorias}
                                    categoriaUuid={dadosCategoria.uuid}
                                    pizza={produto}
                                    excluir={handleOpenExcluirProduto}
                                />
                            }
                            if (produto.tipo === TipoProduto.industrializado && dadosCategoria.uuid) {
                                return <CardIndustrializadoCardapio
                                    key={produto.uuid}
                                    handleRefresh={handleRefresh}
                                    categorias={categorias}
                                    categoriaUuid={dadosCategoria.uuid}
                                    produto={produto}
                                    excluir={handleOpenExcluirProduto}
                                />
                            }
                            if (produto.tipo === TipoProduto.preparado && dadosCategoria.uuid) {
                                return <CardPreparadoCardapio
                                    key={produto.uuid}
                                    handleRefresh={handleRefresh}
                                    categorias={categorias}
                                    categoriaUuid={dadosCategoria.uuid}
                                    produto={produto}
                                    excluir={handleOpenExcluirProduto}
                                />
                            }
                            if (produto.tipo === TipoProduto.peso && dadosCategoria.uuid) {
                                return <CardPesoCardapio
                                    key={produto.uuid}
                                    handleRefresh={handleRefresh}
                                    categorias={categorias}
                                    categoriaUuid={dadosCategoria.uuid}
                                    produto={produto}
                                    excluir={handleOpenExcluirProduto}
                                />
                            }
                            if (produto.tipo === TipoProduto.combo && dadosCategoria.uuid) {
                                return <CardPreparadoCardapio
                                    key={produto.uuid}
                                    handleRefresh={handleRefresh}
                                    categorias={categorias}
                                    categoriaUuid={dadosCategoria.uuid}
                                    produto={produto}
                                    excluir={handleOpenExcluirProduto}
                                />
                            }
                        })}
                    </BodyCardContainer>
                ) : (
                    <BodySemProdutosCardContainer>
                        Nenhum produto cadastrado
                    </BodySemProdutosCardContainer>
                )}
            </AccordionDetails>
        </Accordion>
    )
}
